import { extractPalette, rgbToHex } from '@auctisync/color';

/**
 * Read a house's brand colours out of the logo they just picked.
 *
 * Jim, 2026-09-17: *"when they upload their logo, the colors should be detected.
 * They can specify different ones after that."*
 *
 * 🔴 THE ALGORITHM WAS ALREADY BUILT AND WIRED TO NOTHING. `extractPalette` in
 * `@auctisync/color` has existed since the §7 branding spec — deterministic,
 * unit-tested, $0, no LLM — with zero callers anywhere in the repo. This file is
 * the twenty lines of Canvas plumbing that were missing. Worth knowing before
 * anyone reaches for a colour-extraction library.
 *
 * ⚠️ IT READS THE LOCAL FILE, NOT THE UPLOADED URL, and that is not an
 * optimisation. Logos are served from `images.auctisync.com`, a different origin
 * from the admin app; drawing a cross-origin image onto a canvas TAINTS it and
 * `getImageData` then throws a SecurityError. A blob URL from the File the owner
 * just picked is same-origin, so the read simply works — and the colours appear
 * before the upload round-trip rather than after it.
 */
export interface DetectedColours {
  primary: string;
  accent: string;
  dark: string;
}

/** Longest we will wait for a browser to decode a logo before giving up. */
const DECODE_TIMEOUT_MS = 5000;

/**
 * Downscale target. Sampling every pixel of a 4000px logo buys nothing — the
 * buckets are 16 levels per channel — and costs a large `getImageData`
 * allocation on a phone. 160px on the long edge keeps thousands of samples,
 * which is far more than the quantizer needs.
 */
const SAMPLE_EDGE = 160;

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(() => {
      reject(new Error('decode timed out'));
    }, DECODE_TIMEOUT_MS);
    img.onload = () => {
      clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error('could not decode image'));
    };
    img.src = url;
  });
}

/**
 * The three colours a logo suggests, or null when it cannot say.
 *
 * 🔴 NULL ON A MONOCHROME LOGO, DELIBERATELY. A black-and-white or single-grey
 * lockup has no brand hue to find, and `extractPalette` says so via
 * `isMonochrome`. Filling the fields with near-black and near-white would be
 * worse than leaving them empty: the house would either accept three greys as
 * their brand, or have to undo three fields we guessed at. Silence is the honest
 * answer.
 *
 * Never throws. Every failure path — an undecodable file, a tainted canvas, a
 * browser with no 2D context, a zero-size image — returns null, because this is
 * a convenience on top of an upload that must succeed regardless. A house whose
 * logo we cannot read still gets their logo; they just type their own colours,
 * which is exactly what they did before this existed.
 */
export async function detectLogoColours(file: File): Promise<DetectedColours | null> {
  let url: string | null = null;
  try {
    url = URL.createObjectURL(file);
    const img = await loadImage(url);

    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!(w > 0) || !(h > 0)) return null;

    // An SVG with no intrinsic size decodes to 0×0 in some browsers and to the
    // viewport in others; the guard above catches the first case and the scale
    // below bounds the second.
    const scale = Math.min(1, SAMPLE_EDGE / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));

    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (ctx === null) return null;
    ctx.drawImage(img, 0, 0, cw, ch);

    const { data } = ctx.getImageData(0, 0, cw, ch);
    const palette = extractPalette(data);
    if (palette.isMonochrome) return null;

    return {
      primary: rgbToHex(palette.dominant),
      accent: rgbToHex(palette.accent),
      dark: rgbToHex(palette.muted),
    };
  } catch {
    // Includes `extractPalette` throwing on a fully transparent image — a real
    // case for a logo that is all alpha, and not worth distinguishing here.
    return null;
  } finally {
    if (url !== null) URL.revokeObjectURL(url);
  }
}
